'use client';

import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { priceInfo as getPriceInfo } from '@/utils/price';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { getCampaignTrackingCommonProperties } from '../../hooks/campaignTrackingProperties';
import { useCampaignPageTracking } from '../../hooks/useCampaignPageTracking';
import type { MarketingRuntimeConfig } from '../../types';
import type { CollaborationBannerData } from './types';

export const COLLABORATION_TRACKING_MODULES = {
  seriesProduct: '联名页-系列商品',
  gallery: '联名页-媒体图集',
  productList: '联名页-商品列表',
} as const;

export type CollaborationAssetType = '视频' | '图片';
export type CollaborationMediaClickArea = '播放按钮' | '左侧点击' | '右侧点击';

type TrackingProduct = Record<string, unknown>;

function asTrackingProduct(value: unknown): TrackingProduct {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as TrackingProduct)
    : {};
}

function trackingText(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

function productProperties(value: unknown) {
  const product = asTrackingProduct(value);
  return {
    product_sku:
      trackingText(product.shop_sku) || trackingText(product.shopSku) || trackingText(product.sku),
    product_id: trackingText(product.id),
    product_name: trackingText(product.short_name) || trackingText(product.name),
  };
}

function itemProperties(
  value: unknown,
  index: number,
  price: { price?: unknown; sell_price?: unknown },
) {
  return {
    ...productProperties(value),
    product_price: trackingText(price.price),
    product_current_price: trackingText(price.sell_price),
    product_index: index,
  };
}

export function useCollaborationEventTracking(runtimeConfig: MarketingRuntimeConfig) {
  const { trackEvent } = useReporting();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const mediaExposureRef = useRef(new Set<string>());
  const commonProperties = useMemo(
    () => getCampaignTrackingCommonProperties(runtimeConfig),
    [runtimeConfig],
  );

  const report = useCallback(
    (eventName: string, properties: Record<string, unknown>) => {
      try {
        trackEvent({ eventName, properties: { ...commonProperties, ...properties } }, [
          ProviderType.SENSORS,
        ]);
      } catch (error) {
        logger.error('Collaboration campaign tracking error:', error);
      }
    },
    [commonProperties, trackEvent],
  );

  const trackBanner = useCallback(
    (
      eventName: 'CampaignPageValidMkt_Exp' | 'CampaignPageValidMkt_Click',
      banner: Pick<CollaborationBannerData, 'materialType' | 'moduleId' | 'title'>,
    ) => {
      report(eventName, {
        asset_type: banner.materialType === 'video' ? '视频' : '图片',
      });
    },
    [report],
  );

  const trackSeriesProduct = useCallback(
    (
      eventName: 'CampaignPagecobrand_component_Exp' | 'CampaignPagecobrand_component_Click',
      product: unknown,
      mktIndex: number,
      productIndex: number,
      tabName: string,
    ) => {
      report(eventName, {
        mkt_index: mktIndex,
        product_index: productIndex,
        tab_name: tabName,
        ...productProperties(product),
      });
    },
    [report],
  );

  const trackMedia = useCallback(
    (
      eventName: 'CampaignPageMedia_Exp' | 'CampaignPageMedia_Click',
      mktIndex: number,
      assetType: CollaborationAssetType,
      product?: unknown,
      clickArea?: CollaborationMediaClickArea,
    ) => {
      report(eventName, {
        mkt_index: mktIndex,
        asset_type: assetType,
        ...(product ? productProperties(product) : {}),
        ...(clickArea ? { click_area: clickArea } : {}),
      });
    },
    [report],
  );

  const trackItem = useCallback(
    (
      eventName:
        | 'CampaignPageItemList_Exp'
        | 'CampaignPageItemList_Click'
        | 'CampaignPageYouAlsoLikeMkt_Exp'
        | 'CampaignPageYouAlsoLikeMkt_Click',
      product: unknown,
      index: number,
    ) => {
      const trackingProduct = asTrackingProduct(product);
      const price = getPriceInfo(userInfo, trackingProduct, currency) as {
        price?: unknown;
        sell_price?: unknown;
      };
      report(eventName, itemProperties(trackingProduct, index, price));
    },
    [currency, report, userInfo],
  );

  return {
    trackBannerExposure: (
      banner: Pick<CollaborationBannerData, 'materialType' | 'moduleId' | 'title'>,
    ) => trackBanner('CampaignPageValidMkt_Exp', banner),
    trackBannerClick: (
      banner: Pick<CollaborationBannerData, 'materialType' | 'moduleId' | 'title'>,
    ) => trackBanner('CampaignPageValidMkt_Click', banner),
    trackSeriesNavExposure: (tabName: string, _index: number) =>
      report('CampaignPageTab_Exp', { tab_name: tabName }),
    trackSeriesNavClick: (tabName: string, _index: number) =>
      report('CampaignPageTab_Click', { tab_name: tabName }),
    trackSeriesProductClick: (
      product: unknown,
      mktIndex: number,
      productIndex: number,
      tabName: string,
    ) =>
      trackSeriesProduct(
        'CampaignPagecobrand_component_Click',
        product,
        mktIndex,
        productIndex,
        tabName,
      ),
    trackSeriesProductExposure: (
      product: unknown,
      mktIndex: number,
      productIndex: number,
      tabName: string,
    ) =>
      trackSeriesProduct(
        'CampaignPagecobrand_component_Exp',
        product,
        mktIndex,
        productIndex,
        tabName,
      ),
    trackMediaExposure: (
      mktIndex: number,
      assetType: CollaborationAssetType,
      product?: unknown,
    ) => {
      const key = assetType + ':' + mktIndex + ':' + productProperties(product).product_sku;
      if (mediaExposureRef.current.has(key)) return;
      mediaExposureRef.current.add(key);
      trackMedia('CampaignPageMedia_Exp', mktIndex, assetType, product);
    },
    trackMediaClick: (
      mktIndex: number,
      assetType: CollaborationAssetType,
      clickArea: CollaborationMediaClickArea,
      product?: unknown,
    ) => trackMedia('CampaignPageMedia_Click', mktIndex, assetType, product, clickArea),
    trackProductListClick: (product: unknown, index: number, _title: string) =>
      trackItem('CampaignPageItemList_Click', product, index),
    trackProductListExposure: (product: unknown, index: number, _title: string) =>
      trackItem('CampaignPageItemList_Exp', product, index),
    trackYouAlsoLikeClick: (product: unknown, index: number, _title: string) =>
      trackItem('CampaignPageYouAlsoLikeMkt_Click', product, index),
    trackYouAlsoLikeExposure: (product: unknown, index: number, _title: string) =>
      trackItem('CampaignPageYouAlsoLikeMkt_Exp', product, index),
  };
}

export function useCollaborationTracking(runtimeConfig: MarketingRuntimeConfig) {
  const pageTracking = useCampaignPageTracking(runtimeConfig);
  const eventTracking = useCollaborationEventTracking(runtimeConfig);
  const { trackCollectButtonClick, trackPageLeave, trackPageView } = pageTracking;

  useEffect(() => {
    trackPageView();
    return trackPageLeave;
  }, [trackPageLeave, trackPageView]);

  return {
    ...eventTracking,
    trackSeriesProductCollect: (product: unknown, index: number, collectType: string) =>
      trackCollectButtonClick(
        product,
        index,
        COLLABORATION_TRACKING_MODULES.seriesProduct,
        collectType,
      ),
    trackGalleryProductCollect: (product: unknown, index: number, collectType: string) =>
      trackCollectButtonClick(product, index, COLLABORATION_TRACKING_MODULES.gallery, collectType),
    trackProductListCollect: (product: unknown, index: number, collectType: string) =>
      trackCollectButtonClick(
        product,
        index,
        COLLABORATION_TRACKING_MODULES.productList,
        collectType,
      ),
  };
}
